import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/SiteLayout";
import {
  CheckCircle2,
  Leaf,
  Car,
  Scissors,
  ShieldCheck,
  Clock4,
  Sparkles,
  ArrowRight,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1598714805247-5dd440d87124?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjBncmVlbiUyMGxhd24lMjBnYXJkZW4lMjBob3VzZXxlbnwwfHx8fDE3ODE2MTkzNzF8MA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    id: "grasklippning",
    title: "Gräsklippning och trimmning",
    desc: "Jämn klippning + prydlig kanttrimning — gräsmattan ser ut som ny.",
    img: "https://images.unsplash.com/photo-1458245201577-fc8a130b8829?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxsYXduJTIwbW93ZXIlMjBncmFzc3xlbnwwfHx8fDE3ODE2MTkzNzF8MA&ixlib=rb-4.1.0&q=85",
    icon: Leaf,
    pricing: "Från 1 kr/kvm",
  },
  {
    id: "hacktrimning",
    title: "Häcktrimning",
    desc: "Rena linjer och välformade häckar som lyfter hela tomten.",
    img: "https://images.unsplash.com/photo-1729938825151-483cda78c513?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxoZWRnZSUyMHRyaW1taW5nJTIwZ2FyZGVufGVufDB8fHx8MTc4MTYxOTM3MHww&ixlib=rb-4.1.0&q=85",
    icon: Scissors,
    pricing: "Från 350 kr",
  },
  {
    id: "biltvatt",
    title: "Biltvätt",
    desc: "Skonsam, noggrann handtvätt — bilen ser ut som ny igen.",
    img: "https://images.unsplash.com/photo-1611239179213-d972da54091a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxjYXIlMjB3YXNoaW5nJTIwb3V0ZG9vcnxlbnwwfHx8fDE3ODE2MTkzNzB8MA&ixlib=rb-4.1.0&q=85",
    icon: Car,
    pricing: "Fast pris 350 kr",
  },
];

const trust = [
  {
    icon: ShieldCheck,
    title: "Pålitliga",
    desc: "Vi håller tider och kommunicerar tydligt — alltid.",
  },
  {
    icon: Sparkles,
    title: "Noggranna",
    desc: "Detaljerna gör skillnaden. Inget jobb lämnas halvfärdigt.",
  },
  {
    icon: Clock4,
    title: "Problemfria",
    desc: "Boka, luta dig tillbaka och njut av resultatet.",
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Header />

      {/* HERO */}
      <section className="relative pt-24 sm:pt-28 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={HERO_IMG}
            alt="Vacker grön gräsmatta i Vänge"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/70 to-white" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-green-50 text-green-700 ring-1 ring-green-200 px-3 py-1 text-xs font-semibold tracking-wide">
              <Leaf size={14} /> Lokalt företag i Vänge
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900">
              Professionell gräsklippning, häcktrimning &amp;{" "}
              <span className="text-green-600">biltvätt</span> — utan krångel.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Pålitliga, noggranna och problemfria tjänster för ditt hem och din
              trädgård. Få ett snabbt prisförslag på 30 sekunder — ingen
              registrering krävs.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/boka"
                data-testid="hero-boka-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-green-600 hover:bg-green-700 px-7 py-4 text-base font-semibold text-white shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Boka nu <ArrowRight size={18} />
              </Link>
              <a
                href="#tjanster"
                className="inline-flex items-center justify-center rounded-full bg-white ring-1 ring-slate-300 hover:bg-slate-50 px-7 py-4 text-base font-semibold text-slate-900 transition-colors"
                data-testid="hero-services-link"
              >
                Se våra tjänster
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              {["Rättvisa kvm-priser", "Lokalt i Vänge", "−15% på abonnemang"].map(
                (t) => (
                  <div key={t} className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-green-600" />
                    <span>{t}</span>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        id="tjanster"
        className="py-16 sm:py-24 bg-white"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-green-700 font-semibold">
              Våra tjänster
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Allt ditt hem behöver — utfört av lokala proffs.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Tre tjänster, en pålitlig leverantör. Välj en eller kombinera flera.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.id}
                  className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:border-green-300 transition-all duration-300"
                  data-testid={`service-card-${s.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-xs font-semibold text-slate-800">
                      <Icon size={14} className="text-green-600" />
                      {s.pricing}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-900">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-slate-600 leading-relaxed">
                      {s.desc}
                    </p>
                    <Link
                      to="/boka"
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-green-700 hover:text-green-800"
                      data-testid={`service-card-link-${s.id}`}
                    >
                      Få prisförslag <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-green-600 px-6 sm:px-12 py-12 sm:py-16 text-center shadow-xl">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Redo att slippa krånglet?
            </h2>
            <p className="mt-3 text-green-50 text-base sm:text-lg max-w-xl mx-auto">
              Få ett tydligt prisförslag direkt — utan väntan, utan
              överraskningar.
            </p>
            <Link
              to="/boka"
              data-testid="mid-boka-btn"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-white text-green-700 hover:bg-green-50 px-8 py-4 text-base font-bold shadow-md transition-colors"
            >
              Boka nu <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section
        id="varfor-oss"
        className="py-16 sm:py-24 bg-slate-50"
        data-testid="why-us-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-green-700 font-semibold">
              Varför oss
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Lokal kunskap. Hantverksmässig precision.
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {trust.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.title}
                  className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8"
                  data-testid={`trust-card-${t.title.toLowerCase()}`}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-green-50 text-green-700 ring-1 ring-green-200">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-slate-900">
                    {t.title}
                  </h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">
                    {t.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
